import { Container } from '@ag1/ui';
import clsx from 'clsx';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useHorizontalScrollToElement } from 'shared/hooks/useHorizontalScrollToElement';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { AnchorNavigation } from 'types/studio/studio';
import styles from './AnchorNavigation.module.scss';
import { useScrollCardsNavigation } from './useScrollCardsNavigation';
import { getCardNumberText } from './utils';

type Props = {
  variant: AnchorNavigation['variant'];
  anchors: AnchorNavigation['anchors'];
  addNumbers: AnchorNavigation['addNumbers'];
  theme: AnchorNavigation['theme'];
  bgColor: AnchorNavigation['bgColor'];
  isActiveAnchor: (link: string) => boolean;
  setActiveLinkWithScroll: (link: string) => void;
  activeLink?: string;
  cardsNavbarRef: React.MutableRefObject<HTMLDivElement | null>;
  top: string | number;
};

export const LinksNavigation = forwardRef<HTMLDivElement | null, Props>(
  (
    {
      variant,
      anchors,
      theme,
      bgColor,
      addNumbers,
      isActiveAnchor,
      setActiveLinkWithScroll,
      activeLink,
      cardsNavbarRef,
      top
    },
    ref
  ) => {
    const innerNavRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => innerNavRef.current as HTMLDivElement);

    const activeItemRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (innerNavRef.current) {
        innerNavRef.current.style.top = `${top}`;
      }
    }, [top]);

    const { cardsNavSticky } = useScrollCardsNavigation({
      variant,
      navTopPadding: innerNavRef.current?.offsetHeight || 0,
      cardsNavbarRef,
      linksNavbarRef: innerNavRef
    });

    useHorizontalScrollToElement({
      containerRef: innerNavRef,
      itemRef: activeItemRef,
      dependency: activeLink
    });

    return (
      <nav
        className={clsx(
          styles.nav,
          variant === 'cards' && styles.stickyCards,
          variant === 'cards' && cardsNavSticky && styles.stickyIn,
          variant === 'cards' && !cardsNavSticky && styles.stickyOut,
          getThemeCSSClass(theme),
          bgColor === 'stone' && styles.stone
        )}
        role="navigation"
        data-variant="links"
        ref={innerNavRef}>
        <Container narrow>
          <ul className={styles.list}>
            {anchors &&
              anchors.map((anchor, index) => (
                <li
                  key={anchor.anchorTitle}
                  className={clsx(styles.listItem, getThemeCSSClass(theme))}>
                  <button
                    type="button"
                    className={clsx(
                      styles.buttonLink,
                      isActiveAnchor(anchor.anchorLink) && styles.active,
                      addNumbers && styles.withNumbers,
                      !addNumbers && styles.alignEnd,
                      getThemeCSSClass(theme)
                    )}
                    ref={isActiveAnchor(anchor.anchorLink) ? activeItemRef : null}
                    onClick={() => setActiveLinkWithScroll(anchor.anchorLink)}>
                    {variant === 'cards' && addNumbers && (
                      <span className={styles.number}>{getCardNumberText(index + 1)}</span>
                    )}
                    <span className={styles.text}>{anchor.anchorTitle}</span>
                  </button>
                </li>
              ))}
          </ul>
        </Container>
      </nav>
    );
  }
);

LinksNavigation.displayName = 'LinksNavigation';
